import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

const PRODUCT_AREA_ICONS = {
  apply: "ph-clipboard-text",
  payments: "ph-credit-card",
  credentials: "ph-seal-check",
  academy: "ph-graduation-cap",
  workplace: "ph-briefcase",
  hub: "ph-user-circle",
  system: "ph-wrench",
  events: "ph-calendar",
}

export default class extends Controller {
  static targets = ["badge", "drawer", "list", "empty", "markAllReadButton"]
  static values = {
    unreadCountUrl: String,
    markReadUrlTemplate: String,
    markAllReadUrl: String,
    listUrl: String,
  }

  connect() {
    this.page = 0
    this.hasMore = true
    this.loading = false
    this.unreadCount = 0
    this.subscription = null
    this.boundOutsideClick = this.onOutsideClick.bind(this)

    this.refreshUnreadCount()
    this.subscribeToCable()
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
    document.removeEventListener("click", this.boundOutsideClick)
  }

  subscribeToCable() {
    try {
      this.consumer = this.consumer || createConsumer()
      this.subscription = this.consumer.subscriptions.create(
        { channel: "NotificationChannel" },
        {
          received: (data) => this.onBroadcast(data),
        }
      )
    } catch (e) {
      console.warn("[notification-bell] failed to subscribe to NotificationChannel", e)
    }
  }

  onBroadcast(notification) {
    this.unreadCount += 1
    this.renderBadge()
    if (this.hasDrawerTarget && !this.drawerTarget.hidden) {
      this.prependNotification(notification)
    }
  }

  toggle(event) {
    event.preventDefault()
    if (this.drawerTarget.hidden) {
      this.open()
    } else {
      this.close()
    }
  }

  open() {
    this.drawerTarget.hidden = false
    document.addEventListener("click", this.boundOutsideClick)
    if (this.page === 0) {
      this.loadPage()
    }
  }

  close() {
    this.drawerTarget.hidden = true
    document.removeEventListener("click", this.boundOutsideClick)
  }

  onOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  async refreshUnreadCount() {
    if (!this.unreadCountUrlValue) return
    try {
      const res = await fetch(this.unreadCountUrlValue, {
        headers: { Accept: "application/json" },
      })
      if (!res.ok) return
      const json = await res.json()
      this.unreadCount = json.count || 0
      this.renderBadge()
    } catch (e) {
      console.warn("[notification-bell] failed to fetch unread count", e)
    }
  }

  renderBadge() {
    if (!this.hasBadgeTarget) return
    if (this.unreadCount > 0) {
      this.badgeTarget.hidden = false
      this.badgeTarget.textContent = this.unreadCount > 99 ? "99+" : String(this.unreadCount)
    } else {
      this.badgeTarget.hidden = true
      this.badgeTarget.textContent = "0"
    }
  }

  async loadPage() {
    if (this.loading || !this.hasMore) return
    this.loading = true
    const url = `${this.listUrlValue}${this.listUrlValue.includes("?") ? "&" : "?"}page=${this.page + 1}`
    try {
      const res = await fetch(url, { headers: { Accept: "application/json" } })
      if (!res.ok) {
        this.loading = false
        return
      }
      const json = await res.json()
      this.page = json.page || this.page + 1
      this.hasMore = !!json.has_more
      this.unreadCount = json.unread_count
      this.renderBadge()

      if ((json.notifications || []).length === 0 && this.page === 1) {
        this.showEmpty()
      } else {
        this.hideEmpty()
        json.notifications.forEach((n) => this.appendNotification(n))
      }
    } catch (e) {
      console.warn("[notification-bell] failed to load notifications", e)
    } finally {
      this.loading = false
    }
  }

  showEmpty() {
    if (this.hasEmptyTarget) this.emptyTarget.hidden = false
  }

  hideEmpty() {
    if (this.hasEmptyTarget) this.emptyTarget.hidden = true
  }

  appendNotification(notification) {
    const node = this.buildNode(notification)
    this.listTarget.appendChild(node)
  }

  prependNotification(notification) {
    const node = this.buildNode(notification)
    if (this.hasEmptyTarget) this.emptyTarget.hidden = true
    this.listTarget.insertBefore(node, this.listTarget.firstChild)
  }

  buildNode(notification) {
    const wrapper = document.createElement("a")
    wrapper.className = `notification-drawer-card ${notification.read ? "read" : "unread"}`
    // Route clicks through the Hub's read endpoint so the server marks the
    // notification read and forwards to its (host-relative) destination,
    // instead of jumping straight to a stored absolute URL.
    wrapper.href = notification.action_url ? this.readUrlFor(notification.id) : "#"
    wrapper.dataset.notificationId = notification.id

    const icon = PRODUCT_AREA_ICONS[notification.product_area] || "ph-bell"
    wrapper.innerHTML = `
      <div class="notification-drawer-card-icon"><i class="ph ${icon}"></i></div>
      <div class="notification-drawer-card-body">
        <div class="notification-drawer-card-title">${escapeHtml(notification.title)}</div>
        ${notification.body ? `<div class="notification-drawer-card-message">${escapeHtml(truncate(notification.body, 120))}</div>` : ""}
        ${notification.deadline ? `<div class="notification-drawer-card-deadline">${escapeHtml(notification.deadline)}</div>` : ""}
        ${notification.action_url && notification.action_label ? `<div class="notification-drawer-card-action">${escapeHtml(notification.action_label)}</div>` : ""}
        <div class="notification-drawer-card-time">${formatRelativeTime(notification.created_at)}</div>
      </div>
    `
    return wrapper
  }

  readUrlFor(id) {
    return this.markReadUrlTemplateValue.replace(/\/0\/read$/, `/${id}/read`)
  }

  async markRead(id) {
    const url = this.readUrlFor(id)
    try {
      await fetch(url, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrfToken(),
        },
      })
      const node = this.listTarget.querySelector(`[data-notification-id='${id}']`)
      if (node) {
        node.classList.remove("unread")
        node.classList.add("read")
      }
      this.unreadCount = Math.max(0, this.unreadCount - 1)
      this.renderBadge()
    } catch (e) {
      console.warn("[notification-bell] failed to mark notification as read", e)
    }
  }

  async markAllRead(event) {
    event.preventDefault()
    try {
      await fetch(this.markAllReadUrlValue, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrfToken(),
        },
      })
      this.listTarget.querySelectorAll(".notification-drawer-card.unread").forEach((node) => {
        node.classList.remove("unread")
        node.classList.add("read")
      })
      this.unreadCount = 0
      this.renderBadge()
    } catch (e) {
      console.warn("[notification-bell] failed to mark all read", e)
    }
  }

  onScroll(event) {
    const el = event.currentTarget
    if (!this.hasMore || this.loading) return
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 64) {
      this.loadPage()
    }
  }
}

function csrfToken() {
  const tag = document.querySelector("meta[name='csrf-token']")
  return tag ? tag.content : ""
}

function escapeHtml(text) {
  if (text == null) return ""
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;")
}

function truncate(text, length) {
  if (!text) return ""
  return text.length > length ? `${text.slice(0, length)}…` : text
}

function formatRelativeTime(iso) {
  if (!iso) return ""
  const date = new Date(iso)
  const diffSec = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000))
  if (diffSec < 60) return `${diffSec}s ago`
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`
  return `${Math.floor(diffSec / 86400)}d ago`
}
